import type { Identifier } from "ra-core";

import { isIsoDay } from "@/components/market-intel/dates";
import {
  COMPETITOR_LINES,
  isCompetitorLine,
  isTrackingStatus,
  TRACKING_STATUSES,
} from "@/components/market-intel/labels";
import type {
  CompetitorLine,
  MarketCompetitor,
  MarketCompetitorUpdate,
  TrackingStatus,
} from "@/components/market-intel/types";

export type LineFilter = CompetitorLine | "all";
export type StatusFilter = TrackingStatus | "all";

export const LINE_FILTERS: readonly LineFilter[] = ["all", ...COMPETITOR_LINES];
export const STATUS_FILTERS: readonly StatusFilter[] = [
  "all",
  ...TRACKING_STATUSES,
];

/** Competitors matching both filters (rows without a status count as "vigilar"). */
export const filterCompetitors = (
  competitors: readonly MarketCompetitor[],
  line: LineFilter,
  status: StatusFilter,
): MarketCompetitor[] =>
  competitors.filter(
    (competitor) =>
      (line === "all" || competitor.line === line) &&
      (status === "all" ||
        (competitor.tracking_status ?? "vigilar") === status),
  );

/**
 * The `useUpdate` payload of a quick tracking status change, or null when
 * nothing would change (same value or a value the database would reject).
 */
export const trackingStatusPatch = (
  competitor: MarketCompetitor,
  status: TrackingStatus,
): {
  id: Identifier;
  data: Pick<MarketCompetitor, "tracking_status">;
  previousData: MarketCompetitor;
} | null => {
  if (!isTrackingStatus(status) || competitor.tracking_status === status)
    return null;
  return {
    id: competitor.id,
    data: { tracking_status: status },
    previousData: competitor,
  };
};

/** Latest news / note date per competitor id. */
export const latestUpdateByCompetitor = (
  updates: readonly MarketCompetitorUpdate[],
): Map<string, string> => {
  const latest = new Map<string, string>();
  for (const update of updates) {
    const key = String(update.competitor_id);
    const current = latest.get(key);
    if (!current || update.update_date > current)
      latest.set(key, update.update_date);
  }
  return latest;
};

/** Newest first; same day: the most recently inserted first. */
export const sortTimeline = (
  updates: readonly MarketCompetitorUpdate[],
): MarketCompetitorUpdate[] =>
  [...updates].sort(
    (a, b) =>
      b.update_date.localeCompare(a.update_date) || Number(b.id) - Number(a.id),
  );

/** Notes written in the app can be deleted by their author only (RLS). */
export const canDeleteUpdate = (
  update: MarketCompetitorUpdate,
  salesId: Identifier | null | undefined,
): boolean =>
  update.kind === "nota" &&
  salesId != null &&
  update.sales_id != null &&
  String(update.sales_id) === String(salesId);

// ---------- form ----------

const TEXT_FIELDS = [
  "name",
  "website",
  "location",
  "entry_price",
  "pricing",
  "value_proposition",
  "channels",
  "description",
  "strengths",
  "weaknesses",
  "notes",
  "conclusion",
  "logo_url",
  "last_reviewed_at",
] as const;

type TextField = (typeof TEXT_FIELDS)[number];

export type CompetitorFormValues = Record<TextField, string> & {
  line: CompetitorLine;
  tracking_status: TrackingStatus;
};

export type CompetitorPayload = Record<
  Exclude<TextField, "name">,
  string | null
> & {
  name: string;
  line: CompetitorLine;
  tracking_status: TrackingStatus;
};

export const competitorFormDefaults = (
  competitor?: Partial<MarketCompetitor>,
): CompetitorFormValues => ({
  ...(Object.fromEntries(
    TEXT_FIELDS.map((field) => [field, competitor?.[field] ?? ""]),
  ) as Record<TextField, string>),
  line: isCompetitorLine(competitor?.line) ? competitor.line : "otro",
  tracking_status: isTrackingStatus(competitor?.tracking_status)
    ? competitor.tracking_status
    : "vigilar",
});

const MAX_NAME = 120;

/** "example.com" -> "https://example.com"; anything else is kept trimmed. */
export const normalizeWebsite = (value: string): string | null => {
  const url = value.trim();
  if (!url) return null;
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
};

/** Trimmed payload for create/update, or the i18n key of the first error. */
export const normalizeCompetitorForm = (
  values: CompetitorFormValues,
): { ok: true; data: CompetitorPayload } | { ok: false; error: string } => {
  const name = values.name.trim();
  if (!name)
    return { ok: false, error: "market.competitors.form.name_required" };
  if (name.length > MAX_NAME)
    return { ok: false, error: "market.competitors.form.name_too_long" };
  const reviewed = values.last_reviewed_at.trim();
  if (reviewed && !isIsoDay(reviewed))
    return { ok: false, error: "market.competitors.form.date_invalid" };
  const optional = (value: string) => value.trim() || null;
  return {
    ok: true,
    data: {
      name,
      website: normalizeWebsite(values.website),
      location: optional(values.location),
      entry_price: optional(values.entry_price),
      pricing: optional(values.pricing),
      value_proposition: optional(values.value_proposition),
      channels: optional(values.channels),
      description: optional(values.description),
      strengths: optional(values.strengths),
      weaknesses: optional(values.weaknesses),
      notes: optional(values.notes),
      conclusion: optional(values.conclusion),
      logo_url: normalizeWebsite(values.logo_url),
      last_reviewed_at: reviewed || null,
      line: isCompetitorLine(values.line) ? values.line : "otro",
      tracking_status: isTrackingStatus(values.tracking_status)
        ? values.tracking_status
        : "vigilar",
    },
  };
};

const MAX_NOTE = 4000;

/** Payload of a manual timeline note, or the i18n key of the error. */
export const normalizeNote = (
  competitorId: Identifier,
  text: string,
  today: string,
  salesId?: Identifier | null,
):
  | {
      ok: true;
      data: Pick<
        MarketCompetitorUpdate,
        "competitor_id" | "update_date" | "summary" | "kind" | "sales_id"
      >;
    }
  | { ok: false; error: string } => {
  const summary = text.trim();
  if (!summary)
    return { ok: false, error: "market.competitors.note_errors.required" };
  if (summary.length > MAX_NOTE)
    return { ok: false, error: "market.competitors.note_errors.too_long" };
  return {
    ok: true,
    data: {
      competitor_id: competitorId,
      update_date: today,
      summary,
      kind: "nota",
      // The DB trigger fills it too; sending it keeps the demo (FakeRest) honest.
      sales_id: salesId ?? null,
    },
  };
};
